/**
 * TargetsHistoryChart — atteinte des objectifs CA mois par mois (Lot 6).
 * Barres = CA réalisé (vert si objectif atteint), ligne pointillée = objectif.
 */
import React, { useEffect, useState } from 'react';
import {
    ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
    CartesianGrid, Cell,
} from 'recharts';
import { TrendingUp, Loader2 } from 'lucide-react';
import api from '../../lib/api';

function eur(n) {
    if (n === null || n === undefined) return 'n/d';
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n) + ' €';
}

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
        <div className="rounded-lg border px-3 py-2 text-xs shadow-lg"
             style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
            <p className="font-bold mb-1">{label}</p>
            <p>Réalisé : <span className="font-semibold" style={{ color: '#681793' }}>{eur(d.realized)}</span></p>
            <p>Objectif : {eur(d.target)}</p>
            {d.pct !== null && (
                <p className="font-semibold mt-0.5"
                   style={{ color: d.pct >= 100 ? '#16a34a' : d.pct >= 60 ? '#681793' : '#f59e0b' }}>
                    {d.pct} % atteint
                </p>
            )}
        </div>
    );
};

export default function TargetsHistoryChart() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get('/pilotage/targets/history?months=12')
            .then((r) => setData(r.data.months || []))
            .catch(() => setData([]));
    }, []);

    if (data && data.length === 0) return null;

    return (
        <section data-testid="targets-history-chart">
            <h2 className="text-sm font-semibold uppercase tracking-wider mb-2 flex items-center gap-2"
                style={{ color: 'var(--muted-foreground)' }}>
                <TrendingUp className="w-4 h-4" style={{ color: '#681793' }} />
                Objectifs vs réalisé — 12 derniers mois
            </h2>
            <div className="rounded-xl border p-4"
                 style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
                {!data ? (
                    <div className="flex justify-center py-10">
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#681793' }} />
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height={240}>
                        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                            <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false}
                                   tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={36} />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(104,23,147,0.06)' }} />
                            <Bar dataKey="realized" name="Réalisé" radius={[4, 4, 0, 0]} maxBarSize={36}>
                                {data.map((d, i) => (
                                    <Cell key={i}
                                          fill={d.pct !== null && d.pct >= 100 ? '#16a34a' : '#681793'}
                                          fillOpacity={d.realized ? 0.9 : 0.3} />
                                ))}
                            </Bar>
                            <Line dataKey="target" name="Objectif" type="stepAfter"
                                  stroke="#F5B301" strokeWidth={2} strokeDasharray="6 4"
                                  dot={false} connectNulls={false} />
                        </ComposedChart>
                    </ResponsiveContainer>
                )}
                <p className="text-[11px] mt-1" style={{ color: 'var(--muted-foreground)' }}>
                    <span style={{ color: '#F5B301' }}>▪</span> Objectif mensuel (page Objectifs CA)
                    {' · '}<span style={{ color: '#16a34a' }}>▪</span> mois où l'objectif est atteint
                    {' · '}<span style={{ color: '#681793' }}>▪</span> CA réalisé
                </p>
            </div>
        </section>
    );
}
